import React, { useEffect, useState } from 'react';
import { Check, Circle, Clock, Download, Loader2, Lock, Minus, Search, Users } from 'lucide-react';

import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Card, CardContent } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { PaginationControls } from 'components/ui/pagination';
import { Progress } from 'components/ui/progress';
import type { CourseEligibility } from 'types';
import { cn } from 'lib/utils';

type CourseStatus = 'not_assigned' | 'not_started' | 'in_progress' | 'completed';

interface UserCourseProgress {
  courseId: number;
  assigned: boolean;
  eligibility?: CourseEligibility;
  status: CourseStatus;
  progressPercent?: number | null;
}

interface UserRow {
  id: number;
  displayName: string;
  email: string;
  courseProgress: UserCourseProgress[];
}

interface CourseColumn {
  id: number;
  name: string;
}

interface UserProgressTableProps {
  users: UserRow[];
  courses: CourseColumn[];
  selectedUserId?: number;
  onSelectUser: (userId: number) => void;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  onExport: () => void;
  isExporting: boolean;
}

const USERS_PER_PAGE = 10;

const StatusBadge: React.FC<{ status: CourseStatus }> = ({ status }) => {
  if (status === 'not_assigned') {
    return (
      <Badge variant="outline" className="border-muted bg-muted/30 text-xs text-muted-foreground">
        <Minus className="mr-1 size-3" />
        Not assigned
      </Badge>
    );
  }
  if (status === 'not_started') {
    return (
      <Badge variant="outline" className="border-muted text-xs text-muted-foreground">
        <Circle className="mr-1 size-3" />
        Not started
      </Badge>
    );
  }
  if (status === 'in_progress') {
    return (
      <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700 text-xs">
        <Clock className="mr-1 size-3" />
        In progress
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700 text-xs">
      <Check className="mr-1 size-3" />
      Completed
    </Badge>
  );
};

export const UserProgressTable: React.FC<UserProgressTableProps> = ({
  users,
  courses,
  selectedUserId,
  onSelectUser,
  searchQuery,
  onSearchQueryChange,
  onExport,
  isExporting,
}) => {
  const [page, setPage] = useState(1);
  const filteredUsers = users.filter((user) => {
    const normalized = searchQuery.trim().toLowerCase();
    if (!normalized) {
      return true;
    }
    return (
      user.displayName.toLowerCase().includes(normalized)
      || user.email.toLowerCase().includes(normalized)
    );
  });
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / USERS_PER_PAGE));
  const paginatedUsers = filteredUsers.slice((page - 1) * USERS_PER_PAGE, page * USERS_PER_PAGE);

  useEffect(() => {
    setPage((currentPage) => Math.min(currentPage, totalPages));
  }, [totalPages]);

  return (
    <section aria-labelledby="user-progress-heading" className="w-full">
      <Card disableAnimation maxWidth="none" className="w-full bg-white p-0 shadow-elevation-sm">
        <CardContent variant="custom" padding="none">
          <div className="flex flex-col gap-4 border-b border-border px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <h2 id="user-progress-heading" className="text-xl font-semibold text-foreground sm:text-2xl">
                User Progress
              </h2>
              <div className="relative w-full sm:w-72">
                <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="search"
                  aria-label="Search organization users"
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(event) => {
                    setPage(1);
                    onSearchQueryChange(event.target.value);
                  }}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                type="button"
                size="auto"
                data-testid="org-admin-export-user-progress"
                onClick={onExport}
                disabled={isExporting || courses.length === 0 || filteredUsers.length === 0}
              >
                {isExporting ? <Loader2 className="animate-spin" /> : <Download />}
                {isExporting ? 'Exporting...' : 'Export User Progress'}
              </Button>
            </div>
          </div>

          {filteredUsers.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Users className="size-5" />
              </div>
              <h3 className="text-base font-semibold text-foreground">No matching users</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                No users match your search criteria. Try adjusting your search.
              </p>
            </div>
          ) : (
            <div className="w-full overflow-x-auto">
              <div className="min-w-max">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-t border-border">
                      <th className="sm:sticky sm:left-0 z-10 min-w-[200px] bg-white px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                        Display name
                      </th>
                      <th className="sm:sticky sm:left-[200px] z-10 min-w-[220px] border-r border-border bg-white px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                        Email
                      </th>
                      {courses.map((course) => (
                        <th
                          key={course.id}
                          className="min-w-[180px] px-4 py-3 text-left text-sm font-medium text-muted-foreground"
                        >
                          {course.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedUsers.map((user) => (
                      <tr
                        key={user.id}
                        data-testid={`org-admin-user-row-${user.id}`}
                        onClick={() => onSelectUser(user.id)}
                        className={cn(
                          'group cursor-pointer border-t border-border transition-colors hover:bg-accent/50',
                          selectedUserId === user.id && 'bg-accent',
                        )}
                      >
                        <td
                          className={cn(
                            'sm:sticky sm:left-0 z-10 min-w-[200px] px-4 py-3 text-sm font-medium text-foreground transition-colors',
                            selectedUserId === user.id ? 'bg-accent' : 'bg-white group-hover:bg-accent/50',
                          )}
                        >
                          <button type="button" onClick={() => onSelectUser(user.id)} className="rounded text-left hover:underline focus-visible:ring-2 focus-visible:ring-primary">{user.displayName}</button>
                        </td>
                        <td
                          className={cn(
                            'sm:sticky sm:left-[200px] z-10 min-w-[220px] border-r border-border px-4 py-3 text-sm text-muted-foreground transition-colors',
                            selectedUserId === user.id ? 'bg-accent' : 'bg-white group-hover:bg-accent/50',
                          )}
                        >
                          {user.email}
                        </td>
                        {courses.map((course) => {
                          const progress = user.courseProgress.find((item) => item.courseId === course.id);
                          const status = progress?.status || 'not_assigned';
                          const progressPercent = progress?.progressPercent ?? 0;

                          return (
                            <td key={course.id} className="min-w-[180px] px-4 py-3">
                              <div className="space-y-2">
                                <StatusBadge status={status} />
                                {progress?.eligibility?.is_locked && <Badge variant="outline" className="border-amber-200 bg-amber-50 text-xs text-amber-800"><Lock className="mr-1 h-3 w-3" />Locked</Badge>}
                                {progress?.assigned ? (
                                  <div className="flex items-center gap-2">
                                    <Progress value={progressPercent} className="h-1.5 w-16" />
                                    <span className="text-xs text-muted-foreground">
                                      {Math.round(progressPercent)}%
                                    </span>
                                  </div>
                                ) : null}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {filteredUsers.length > USERS_PER_PAGE ? (
                <div className="border-t border-border px-6 py-4">
                  <PaginationControls
                    page={page}
                    pageSize={USERS_PER_PAGE}
                    totalCount={filteredUsers.length}
                    onPageChange={setPage}
                  />
                </div>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
};
